import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getInvite } from '../../lib/api/parents';
import { joinHouseholdWithToken } from '../../lib/api/auth';
import { useAuth } from '../../contexts/AuthContext';
import AuthPage from './AuthPage';
import SafeIcon from '../../common/SafeIcon';

export default function InvitePage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, switchHousehold, loading: authLoading } = useAuth();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('verifying'); // verifying, joining, ready
  const hasJoined = useRef(false);

  // 1. Verify the token exists and is valid
  useEffect(() => {
    getInvite(token)
      .then((data) => {
        setInvite(data);
        setStatus('ready');
      })
      .catch((e) => {
        setError(e.message);
        setStatus('error');
      });
  }, [token]);

  // 2. AUTO-JOIN LOGIC: If user is logged in, join immediately
  useEffect(() => {
    const performAutoJoin = async () => {
      if (user && invite && !hasJoined.current && !authLoading) {
        hasJoined.current = true;
        setStatus('joining');
        try {
          // Join or switch to this household
          const joinData = await joinHouseholdWithToken(user.id, user.email, token);
          
          // Force the context to update
          await switchHousehold(joinData.householdId, joinData.role);
          
          // Teleport to dashboard
          navigate('/', { replace: true });
        } catch (e) {
          setError(e.message);
          setStatus('error');
        }
      }
    };

    performAutoJoin();
  }, [user, invite, authLoading, token, navigate, switchHousehold]);

  // Error State
  if (error || status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="bg-white p-8 rounded-2xl shadow-sm text-center max-w-sm w-full border border-gray-100">
          <SafeIcon icon="AlertTriangle" className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Invite Link Issue</h2>
          <p className="text-gray-600 mb-6 text-sm">{error || "This link is no longer valid."}</p>
          <button 
            onClick={() => navigate('/')} 
            className="w-full py-3 bg-gray-100 text-gray-700 rounded-xl font-bold hover:bg-gray-200 transition-colors"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // Loading / Joining State
  if (status === 'verifying' || status === 'joining' || authLoading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
        <div className="relative">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-indigo-100 border-t-indigo-600"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <SafeIcon icon="Users" className="w-6 h-6 text-indigo-600" />
          </div>
        </div>
        <p className="mt-6 text-gray-900 font-black text-lg">
          {status === 'joining' ? 'Connecting to family...' : 'Verifying link...'}
        </p>
        <p className="text-gray-500 text-sm mt-1">One moment please</p>
      </div>
    );
  }

  // If NOT logged in, show the AuthPage (Login/Register)
  // The AuthPage will handle the token after they sign in
  if (!user) {
    return <AuthPage inviteToken={token} />;
  }

  return null;
}